import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { isValidUsPhone } from '#/shared/lib/format'

export const LOCATION_TYPE_VALUES = [
  'residential',
  'business',
  'dealership',
  'auction',
  'port',
  'terminal',
  'repo_yard',
  'military_base',
  'rental',
  'other',
] as const

export type LocationType = (typeof LOCATION_TYPE_VALUES)[number]

const LOCATION_TYPE_SET = new Set<string>(LOCATION_TYPE_VALUES)

const LOCATION_TYPE_ALIASES: Record<string, LocationType> = {
  dealer: 'dealership',
  res: 'residential',
  biz: 'business',
  military: 'military_base',
  repo: 'repo_yard',
}

export function normalizeLocationType(
  raw: string | null | undefined,
): LocationType | '' {
  if (!raw) return ''
  const lower = raw.toLowerCase().trim()
  if (LOCATION_TYPE_SET.has(lower)) return lower as LocationType
  return LOCATION_TYPE_ALIASES[lower] ?? ''
}

export interface LocationData {
  id?: string
  businessName: string
  address: string
  city: string
  state: string
  zip: string
  contact: string
  phone: string
  mobile: string
  additionalNote: string
  type: string
}

export interface LocationFormData {
  origin: LocationData
  destination: LocationData
}

const EMPTY_LOCATION: LocationData = {
  businessName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  contact: '',
  phone: '',
  mobile: '',
  additionalNote: '',
  type: '',
}

export const EMPTY_LOCATION_DEFAULTS: LocationFormData = {
  origin: { ...EMPTY_LOCATION },
  destination: { ...EMPTY_LOCATION },
}

export function useLocationSchema() {
  const { t } = useTranslation()
  const required = t('forms.validation.required')
  const invalidPhone = t('forms.validation.invalidUsPhone')

  const optionalUsPhone = z
    .string()
    .optional()
    .default('')
    .refine((value) => value === '' || isValidUsPhone(value), {
      message: invalidPhone,
    })

  const locationItem = z.object({
    id: z.string().optional(),
    businessName: z.string().optional().default(''),
    address: z.string().optional().default(''),
    city: z.string().min(1, required),
    state: z.string().min(1, required),
    zip: z.string().min(1, required),
    contact: z.string().optional().default(''),
    phone: optionalUsPhone,
    mobile: optionalUsPhone,
    additionalNote: z.string().optional().default(''),
    type: z.string().optional().default(''),
  })

  return z.object({
    origin: locationItem,
    destination: locationItem,
  })
}
